import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Undo2, X } from "lucide-react";

/**
 * Refund a paid invoice — typically the Security Deposit when a ward is withdrawn.
 * Staff picks destination: Wallet Credit (parent can spend later) OR Cash-out
 * from the drawer.  Audit trail is recorded on the invoice.
 */
export default function RefundButton({ invoiceId, amount, onDone }) {
  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState("wallet");
  const [amt, setAmt] = useState(amount ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices/refund", {
        invoice_id: invoiceId,
        destination,
        amount: amt === "" ? undefined : Number(amt),
        note: note || undefined,
      });
      toast.success(`Refunded ₹${data.refunded_amount} → ${destination}`);
      setOpen(false); if (onDone) onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        data-testid={`refund-open-${invoiceId}`}
        className="text-[10px] font-mono uppercase tracking-widest border border-green-500/50 text-green-400 px-2 py-1 hover:bg-green-500 hover:text-black inline-flex items-center gap-1"
      >
        <Undo2 size={11} /> Refund
      </button>
    );
  }
  return (
    <div className="border border-green-500/40 bg-green-500/5 p-3 w-full" data-testid={`refund-form-${invoiceId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-green-400">Refund invoice</div>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={12} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <button type="button" onClick={() => setDestination("wallet")}
          data-testid={`refund-dest-wallet-${invoiceId}`}
          className={`text-[10px] font-mono uppercase tracking-widest py-1.5 border ${destination === "wallet" ? "border-green-400 bg-green-500/15 text-green-300" : "border-white/15 text-white/60"}`}>
          Wallet Credit
        </button>
        <button type="button" onClick={() => setDestination("cash")}
          data-testid={`refund-dest-cash-${invoiceId}`}
          className={`text-[10px] font-mono uppercase tracking-widest py-1.5 border ${destination === "cash" ? "border-green-400 bg-green-500/15 text-green-300" : "border-white/15 text-white/60"}`}>
          Cash-out (drawer)
        </button>
      </div>
      <input type="number" placeholder="Amount" value={amt}
        data-testid={`refund-amount-${invoiceId}`}
        onChange={(e) => setAmt(e.target.value)}
        className="w-full bg-black/40 border border-white/15 py-1.5 px-2 text-xs mb-2" />
      <input value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Reason (e.g. ward withdrawn)"
        className="w-full bg-black/40 border border-white/15 py-1.5 px-2 text-xs mb-2" />
      <button onClick={submit} disabled={busy}
        data-testid={`refund-confirm-${invoiceId}`}
        className="w-full bg-green-500 text-black py-1.5 text-[11px] font-bold uppercase tracking-widest disabled:opacity-50">
        {busy ? "Refunding…" : `Confirm Refund ₹${amt || 0}`}
      </button>
    </div>
  );
}
